import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  IconButton,
  Toolbar,
  Tooltip,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  BrightnessAuto,
  DarkMode,
  LightMode,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Locale, useI18n } from '../i18n';
import { NAV_ITEMS, NavItem, activeNavId, isNavItemActive } from '../nav/navItems';
import { SETUP_FLOW_PATH, SETUP_PATH } from '../nav/paths';
import { useColorMode } from '../theme/ColorModeProvider';
import { ThemePreference } from '../theme/themeMode';
import FlagIcon from './FlagIcon';

const DRAWER_WIDTH = 248;
const DRAWER_RAIL = 72;
const NAV_OPEN_KEY = 'vcs-nav-open';

const LOCALES: { locale: Locale; labelKey: 'nav.german' | 'nav.english' }[] = [
  { locale: 'de', labelKey: 'nav.german' },
  { locale: 'en', labelKey: 'nav.english' },
];

function readNavOpen(): boolean {
  try {
    const raw = localStorage.getItem(NAV_OPEN_KEY);
    if (raw === '0' || raw === 'false') return false;
    if (raw === '1' || raw === 'true') return true;
  } catch {
    // ignore
  }
  return true;
}

function writeNavOpen(open: boolean): void {
  try {
    localStorage.setItem(NAV_OPEN_KEY, open ? '1' : '0');
  } catch {
    // ignore
  }
}

function themeLabel(t: (k: string) => string, preference: ThemePreference): string {
  if (preference === 'system') return t('nav.themeSystem');
  if (preference === 'light') return t('nav.themeLight');
  return t('nav.themeDark');
}

function ThemeIcon({ preference }: { preference: ThemePreference }) {
  if (preference === 'system') return <BrightnessAuto fontSize="small" />;
  if (preference === 'light') return <LightMode fontSize="small" />;
  return <DarkMode fontSize="small" />;
}

const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale, setLocale } = useI18n();
  const { preference, cyclePreference } = useColorMode();
  const [langAnchor, setLangAnchor] = useState<HTMLElement | null>(null);
  const [navOpen, setNavOpen] = useState<boolean>(() => readNavOpen());
  const [hub, setHub] = useState(false);
  const currentId = activeNavId(location.pathname);
  const collapsed = !isMobile && !navOpen;
  const drawerWidth = isMobile ? DRAWER_WIDTH : (navOpen ? DRAWER_WIDTH : DRAWER_RAIL);
  const navItems = hub ? NAV_ITEMS : NAV_ITEMS.filter((item) => item.id !== 'instances');

  useEffect(() => {
    let gone = false;
    fetch('/api/health')
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!gone && body?.hub === true) setHub(true);
      })
      .catch(() => {});
    return () => {
      gone = true;
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      setNavOpen(false);
      return;
    }
    setNavOpen(readNavOpen());
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) {
      writeNavOpen(navOpen);
    }
  }, [navOpen, isMobile]);

  const toggleNav = () => setNavOpen((prev) => !prev);

  const languageControl = (
    <>
      <Tooltip title={t('nav.language')}>
        <IconButton
          size="small"
          aria-label={t('nav.language')}
          aria-haspopup="menu"
          onClick={(e) => setLangAnchor(e.currentTarget)}
          sx={{ borderRadius: 2 }}
        >
          <FlagIcon locale={locale} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={langAnchor}
        open={Boolean(langAnchor)}
        onClose={() => setLangAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {LOCALES.map((item) => (
          <MenuItem
            key={item.locale}
            selected={item.locale === locale}
            onClick={() => {
              setLocale(item.locale);
              setLangAnchor(null);
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <FlagIcon locale={item.locale} />
            </ListItemIcon>
            <ListItemText primary={t(item.labelKey)} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  const themeControl = (
    <Tooltip title={`${themeLabel(t, preference)} — ${t('nav.themeCycle')}`}>
      <IconButton
        size="small"
        onClick={cyclePreference}
        aria-label={themeLabel(t, preference)}
        sx={{ borderRadius: 2 }}
      >
        <ThemeIcon preference={preference} />
      </IconButton>
    </Tooltip>
  );

  const navigateFromNav = (path: string) => {
    navigate(path === SETUP_PATH ? SETUP_FLOW_PATH : path);
    if (isMobile) {
      setNavOpen(false);
    }
  };

  const renderItem = (item: NavItem, nested: boolean) => {
    const selected = isNavItemActive(item, location.pathname);
    const Icon = item.icon;
    const label = t(item.labelKey);
    const button = (
      <ListItemButton
        selected={selected}
        onClick={() => navigateFromNav(item.path)}
        sx={{
          mb: nested ? 0.25 : 0.5,
          borderRadius: 2,
          py: nested ? 0.65 : 1,
          px: collapsed ? 1 : 1.5,
          minHeight: collapsed ? 44 : undefined,
          justifyContent: collapsed ? 'center' : 'flex-start',
          '&.Mui-selected': {
            bgcolor: 'action.selected',
            '&:hover': { bgcolor: 'action.selected' },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 40,
            justifyContent: 'center',
            color: selected ? 'primary.main' : 'text.secondary',
          }}
        >
          <Icon fontSize="small" />
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontWeight: selected ? 700 : 500,
              fontSize: nested ? 13 : 14,
            }}
          />
        )}
      </ListItemButton>
    );
    if (!collapsed) {
      return button;
    }
    return (
      <Tooltip title={label} placement="right">
        {button}
      </Tooltip>
    );
  };

  const navList = (
    <List dense disablePadding sx={{ flex: 1, overflowY: 'auto', px: collapsed ? 0.75 : 1 }}>
      {navItems.map((item, index) => (
        <React.Fragment key={item.id}>
          {(index === 3 || index === 6 || item.id === 'instances' || item.id === 'settings') && (
            <Box
              sx={{
                mx: collapsed ? 0.5 : 1,
                my: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            />
          )}
          {renderItem(item, false)}
          {item.children
            && isNavItemActive(item, location.pathname)
            && item.children.map((child) => (
            <Box key={child.id} sx={{ pl: collapsed ? 0 : 1.5 }}>
              {renderItem(child, true)}
            </Box>
          ))}
        </React.Fragment>
      ))}
    </List>
  );

  const drawerBody = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', py: 1.5 }}>
      {navList}
    </Box>
  );

  const topBar = (
    <Paper
      square
      elevation={0}
      sx={{
        flexShrink: 0,
        zIndex: (z) => z.zIndex.drawer + 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Toolbar variant="dense" sx={{ gap: 1, minHeight: 56, px: { xs: 1, sm: 2 } }}>
        <Tooltip title={navOpen ? t('nav.menuClose') : t('nav.menuOpen')}>
          <IconButton
            edge="start"
            onClick={toggleNav}
            aria-label={navOpen ? t('nav.menuClose') : t('nav.menuOpen')}
            sx={{ borderRadius: 2 }}
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 750, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
            {t('app.brand')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {t('app.tagline')}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          {languageControl}
          {themeControl}
        </Box>
      </Toolbar>
    </Paper>
  );

  const drawerSx = {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [`& .MuiDrawer-paper`]: {
      width: drawerWidth,
      boxSizing: 'border-box',
      position: 'relative !important',
      top: 'auto',
      height: '100%',
      overflowX: 'hidden',
      borderRight: '1px solid',
      borderColor: 'divider',
      zIndex: 'auto',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
      {topBar}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, minWidth: 0 }}>
        {!isMobile && (
          <Drawer variant="permanent" open sx={drawerSx}>
            {drawerBody}
          </Drawer>
        )}

        {isMobile && (
          <Drawer
            variant="temporary"
            open={navOpen}
            onClose={() => setNavOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              [`& .MuiDrawer-paper`]: {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
              },
            }}
          >
            {drawerBody}
          </Drawer>
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            pb: isMobile ? 9 : 0,
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {children}
          </Box>
        </Box>
      </Box>

      {isMobile && (
        <Paper
          elevation={0}
          sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: (z) => z.zIndex.appBar }}
        >
          <BottomNavigation
            showLabels
            value={currentId}
            onChange={(_, value: string) => {
              const item = navItems.find((n) => n.id === value);
              if (item) navigate(item.path);
            }}
          >
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <BottomNavigationAction
                  key={item.id}
                  label={t(item.labelKey)}
                  value={item.id}
                  icon={<Icon />}
                  sx={{ minWidth: 0, px: 0.5 }}
                />
              );
            })}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

export default AppShell;
